import { reactive, toRefs, computed } from 'vue';

/**
 * Unified dialog state management for UnifiedBrickSchemaManager
 * Consolidates 38+ individual dialog refs into one reactive object
 *
 * Dialog State Structure:
 * - show: boolean - visibility state
 * - mode: string - 'create' or 'edit'
 * - data: any - associated data (typically the entity being edited)
 */
export function useDialogState() {
  const state = reactive({
    // Building hierarchy dialogs
    building: { show: false, mode: 'create', data: null },
    editBuilding: { show: false, mode: 'edit', data: null },
    level: { show: false, mode: 'create', data: null },
    editLevel: { show: false, mode: 'edit', data: null },
    space: { show: false, mode: 'create', data: null },
    editSpace: { show: false, mode: 'edit', data: null },

    // Equipment dialogs
    equipment: { show: false, mode: 'create', data: null },
    editEquipment: { show: false, mode: 'edit', data: null },
    device: { show: false, mode: 'create', data: null },
    editDevice: { show: false, mode: 'edit', data: null },
    point: { show: false, mode: 'create', data: null },
    editPoint: { show: false, mode: 'edit', data: null },

    // Zone & Location dialogs
    zoneSection: { show: false, mode: 'create', data: null },
    location: { show: false, mode: 'create', data: null },
    locationPicker: { show: false, mode: 'create', data: null },

    // Template dialogs
    template: { show: false, mode: 'create', data: null },
    templateManager: { show: false, mode: 'create', data: null },
    templateEditor: { show: false, mode: 'create', data: null },
    saveAsTemplate: { show: false, mode: 'create', data: null },
    compoundTemplate: { show: false, mode: 'create', data: null },
    usageLocations: { show: false, mode: 'create', data: null },

    // Electrical dialogs
    msb: { show: false, mode: 'create', data: null },
    mssb: { show: false, mode: 'create', data: null },
    ddc: { show: false, mode: 'create', data: null },
    ddcAllocation: { show: false, mode: 'create', data: null },
    ddcPoints: { show: false, mode: 'create', data: null },
    powerConnection: { show: false, mode: 'create', data: null },

    // Switchboard dialogs
    addSwitchboard: { show: false, mode: 'create', data: null },
    addSwitchboardDialog: { show: false, mode: 'create', data: null },
    switchboardClassification: { show: false, mode: 'create', data: null },
    unallocated: { show: false, mode: 'create', data: null },
    equipmentAssignment: { show: false, mode: 'create', data: null },

    // Controller dialogs
    assignController: { show: false, mode: 'create', data: null },
    addController: { show: false, mode: 'create', data: null },
    controllerIoUsage: { show: false, mode: 'create', data: null },

    // Other dialogs
    disassemble: { show: false, mode: 'create', data: null },
    legacyAirflowTable: { show: false, mode: 'create', data: null },
    contextMenu: { show: false, mode: 'create', data: null },
    nodeMenu: { show: false, mode: 'create', data: null },
  });

  // Open a dialog with optional mode and data
  const openDialog = (type, mode = 'create', data = null) => {
    if (state[type]) {
      state[type].show = true;
      state[type].mode = mode;
      state[type].data = data;
    } else {
      console.warn(`[useDialogState] Dialog type "${type}" not found`);
    }
  };

  // Close a dialog and reset its state
  const closeDialog = (type) => {
    if (state[type]) {
      state[type].show = false;
      state[type].mode = 'create';
      state[type].data = null;
    } else {
      console.warn(`[useDialogState] Dialog type "${type}" not found`);
    }
  };

  // Close all dialogs
  const closeAllDialogs = () => {
    Object.keys(state).forEach((key) => {
      state[key].show = false;
      state[key].mode = 'create';
      state[key].data = null;
    });
  };

  // Check if a dialog is open
  const isOpen = (type) => state[type]?.show ?? false;

  // Get dialog data
  const getDialogData = (type) => state[type]?.data ?? null;

  // Get dialog mode
  const getDialogMode = (type) => state[type]?.mode ?? 'create';

  // Create computed refs for backward compatibility
  // These allow direct access like: showBuildingDialog.value = true
  const dialogRefs = {};
  Object.keys(state).forEach((key) => {
    const camelCaseKey = `show${key.charAt(0).toUpperCase()}${key.slice(1)}Dialog`;
    dialogRefs[camelCaseKey] = computed({
      get: () => state[key].show,
      set: (value) => { state[key].show = value; },
    });
  });

  return {
    // The full state object
    dialogs: state,

    // Helper methods
    openDialog,
    closeDialog,
    closeAllDialogs,
    isOpen,
    getDialogData,
    getDialogMode,

    // Backward compatible refs
    ...dialogRefs,
  };
}
